"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.viewTreeFromIndexes = exports.buildViewTree = void 0;
/**
 * Indexes `root` in DFS pre-order. A node without a string `axeViewId` is
 * skipped entirely — it is not indexed, and its subtree is not visited.
 */
const buildViewTree = (root) => {
    const viewMap = new Map();
    const parentMap = new Map();
    const childrenMap = new Map();
    const traverse = (view, parent) => {
        const viewId = view.axeViewId;
        if (typeof viewId !== 'string' || viewId.length === 0) {
            return;
        }
        viewMap.set(viewId, view);
        if (parent !== undefined) {
            parentMap.set(viewId, parent);
        }
        const children = view.children ?? [];
        childrenMap.set(viewId, children);
        for (const child of children) {
            traverse(child, view);
        }
    };
    traverse(root, undefined);
    return {
        getView: (viewId) => viewMap.get(viewId),
        getParent: (viewId) => parentMap.get(viewId),
        getChildren: (viewId) => childrenMap.get(viewId) ?? [],
        get viewIds() {
            return Array.from(viewMap.keys());
        },
        get size() {
            return viewMap.size;
        },
        root
    };
};
exports.buildViewTree = buildViewTree;
/**
 * Builds a `ViewTree` from indexes a caller already collected during its own
 * walk, instead of re-traversing the tree with `buildViewTree`.
 *
 * The returned tree RETAINS `viewsById` and `parentsById` by reference, where
 * `buildViewTree` owns private maps: mutating either map afterwards mutates the
 * tree. `viewIds` is therefore the caller's insertion order, and that order is
 * load-bearing — the positional suffix ranks a colliding group by it.
 *
 * Returns `null` for an empty index, or for one carrying an entry keyed on the
 * empty string: every node with an empty `axeViewId` collapses onto that single
 * `''` key, so the ancestry walk would read a last-write-wins parent and emit a
 * plausible but wrong signature. `null` means "skip fingerprinting", a normal
 * outcome rather than an error.
 *
 * Only the empty-id case is guarded. Duplicate non-empty `axeViewId`s
 * last-write-win the same way, here and in `buildViewTree`.
 */
const viewTreeFromIndexes = (viewsById, parentsById, root) => {
    if (viewsById.size === 0 || viewsById.has('')) {
        return null;
    }
    return {
        getView: (viewId) => viewsById.get(viewId),
        getParent: (viewId) => parentsById.get(viewId),
        getChildren: (viewId) => viewsById.get(viewId)?.children ?? [],
        get viewIds() {
            return Array.from(viewsById.keys());
        },
        get size() {
            return viewsById.size;
        },
        root
    };
};
exports.viewTreeFromIndexes = viewTreeFromIndexes;
//# sourceMappingURL=viewTree.js.map